import { A } from "@solidjs/router";
import { Menu, Moon, Settings, Sun } from "lucide-solid";
import { createSignal, type JSX, Show } from "solid-js";
import { CatAvatar } from "@/components/CatAvatar";
import { IdentityDialog } from "@/components/IdentityDialog";
import { LoginDialog } from "@/components/LoginDialog";
import { ChatDialog } from "@/components/ChatDialog";
import { NewCategoryDialog } from "@/components/NewCategoryDialog";
import { SettingsDialog } from "@/components/SettingsDialog";
import { SideNav } from "@/components/SideNav";
import { Button, Drawer, DrawerHeader, HStack, IconButton, Spacer, Stack } from "@/components/ui";
import { ICON_SIZE } from "@/lib/icons";
import { loginDialogOpen, setLoginDialogOpen, signOut } from "@/state/auth";
import { applyBump, applyCreated, applyVisibility, invalidate } from "@/state/board";
import { applyThreadActivity, refetchCategories } from "@/state/categories";
import { identityEditorOpen, setIdentityEditorOpen } from "@/state/identity";
import { chatOpen, openChat, receiveMessage } from "@/state/chat";
import { currentDisplay, isLoggedIn, me, refetchMe } from "@/state/me";
import { onEvent, onReconnect } from "@/state/realtime";
import { setSettingsOpen, settingsOpen } from "@/state/settings";

/** light / dark は root の data-theme で切り替える (soluid の作法)。 */
function ThemeToggle(): JSX.Element {
  const [dark, setDark] = createSignal(
    document.documentElement.getAttribute("data-theme") === "dark",
  );
  const toggle = () => {
    const next = !dark();
    setDark(next);
    document.documentElement.setAttribute("data-theme", next ? "dark" : "light");
    localStorage.setItem("talkers.theme", next ? "dark" : "light");
  };
  const label = () => (dark() ? "ライトテーマにする" : "ダークテーマにする");
  return (
    <IconButton
      variant="ghost"
      aria-label={label()}
      title={label()}
      icon={dark() ? <Moon size={ICON_SIZE} /> : <Sun size={ICON_SIZE} />}
      onClick={toggle}
    />
  );
}

export function AppLayout(props: { children?: JSX.Element }): JSX.Element {
  const [addingCategory, setAddingCategory] = createSignal(false);
  const [menuOpen, setMenuOpen] = createSignal(false);

  /**
   * 一覧とカテゴリはキャッシュしたまま、更新だけを当てる。
   * ここが唯一の受け口 (画面ごとに配線すると、開いていない一覧が古いまま残る)。
   */
  onEvent((event) => {
    if (event.type === "chat.message") receiveMessage(event.message);
    if (event.type === "thread.bumped") {
      applyBump(event.bump);
      applyThreadActivity(event.bump.categoryId, event.bump.lastPostAt, false);
    }
    if (event.type === "thread.created") {
      applyCreated(event.thread);
      const at = event.thread.lastPostAt ?? event.thread.createdAt;
      applyThreadActivity(event.thread.category.id, at, true);
    }
    if (event.type === "thread.visibility") applyVisibility(event.thread);
    if (event.type === "category.changed") void refetchCategories();
  });

  // 切れている間の更新は届かないので、繋ぎ直したら読み直す
  onReconnect(() => {
    invalidate();
    void refetchCategories();
  });

  const [loggingOut, setLoggingOut] = createSignal(false);
  const logout = async () => {
    setLoggingOut(true);
    try {
      await signOut();
      await refetchMe();
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <div class="app">
      <header class="app-header">
        <HStack gap={3} align="center">
          {/* 狭い画面だけに出る。中身はサイドバー + ヘッダーに入り切らない操作 */}
          <IconButton
            class="menu-button"
            variant="ghost"
            aria-label="メニュー"
            title="メニュー"
            icon={<Menu size={ICON_SIZE} />}
            onClick={() => setMenuOpen(true)}
          />
          <A href="/" class="brand">
            talkers
          </A>
          <Spacer />
          {/* まだセッションが無ければ行き先が無いので、ただの表示にする */}
          <Show
            when={me()?.handle}
            fallback={
              <span class="identity-button">
                <CatAvatar avatar={currentDisplay().avatar} size={28} label="あなたのアバター" />
              </span>
            }
          >
            {(handle) => (
              <A href={`/u/${handle()}`} class="identity-button" title="自分のプロフィール">
                <CatAvatar avatar={currentDisplay().avatar} size={28} label="あなたのアバター" />
                <span class="identity-name">{currentDisplay().name}</span>
              </A>
            )}
          </Show>
          <div class="header-actions">
            <HStack gap={3} align="center">
              <Show
                when={isLoggedIn()}
                fallback={
                  <Button variant="neutral" size="sm" onClick={() => setLoginDialogOpen(true)}>
                    ログイン
                  </Button>
                }
              >
                <Button variant="ghost" size="sm" onClick={() => void openChat()}>
                  チャット
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  loading={loggingOut()}
                  onClick={() => void logout()}
                >
                  ログアウト
                </Button>
              </Show>
              <IconButton
                variant="ghost"
                aria-label="設定"
                title="設定"
                icon={<Settings size={ICON_SIZE} />}
                onClick={() => setSettingsOpen(true)}
              />
              <ThemeToggle />
            </HStack>
          </div>
        </HStack>
      </header>

      <div class="app-body">
        <nav class="app-side">
          <SideNav onAddCategory={() => setAddingCategory(true)} />
        </nav>

        <main class="app-main">{props.children}</main>
      </div>

      <Drawer open={menuOpen()} onClose={() => setMenuOpen(false)} side="left" size="sm">
        <DrawerHeader>メニュー</DrawerHeader>
        <div class="menu-body">
          <Stack gap={4}>
            <Stack gap={2}>
              <Show
                when={isLoggedIn()}
                fallback={
                  <Button
                    variant="neutral"
                    size="sm"
                    onClick={() => {
                      setMenuOpen(false);
                      setLoginDialogOpen(true);
                    }}
                  >
                    ログイン
                  </Button>
                }
              >
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setMenuOpen(false);
                    void openChat();
                  }}
                >
                  チャット
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setMenuOpen(false);
                    void logout();
                  }}
                >
                  ログアウト
                </Button>
              </Show>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setMenuOpen(false);
                  setSettingsOpen(true);
                }}
              >
                設定
              </Button>
            </Stack>

            <SideNav
              collapsed
              onNavigate={() => setMenuOpen(false)}
              onAddCategory={() => {
                setMenuOpen(false);
                setAddingCategory(true);
              }}
            />
          </Stack>
        </div>
      </Drawer>

      {/* 開いている間だけマウントして、下書きが前回の状態を持ち越さないようにする */}
      <Show when={identityEditorOpen()}>
        <IdentityDialog onClose={() => setIdentityEditorOpen(false)} />
      </Show>

      <Show when={addingCategory()}>
        <NewCategoryDialog onClose={() => setAddingCategory(false)} />
      </Show>

      <Show when={settingsOpen()}>
        <SettingsDialog onClose={() => setSettingsOpen(false)} />
      </Show>

      <Show when={chatOpen()}>
        <ChatDialog />
      </Show>

      <Show when={loginDialogOpen()}>
        <LoginDialog onClose={() => setLoginDialogOpen(false)} />
      </Show>
    </div>
  );
}
